import { Link } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import { Download, Eye, Link2, MoreVertical, Pencil, Trash2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { toast } from '@app/components/ui/Toast/toast'
import { appLink } from '@app/config/appLink'
import { isAdminAtom, userAtom } from '@app/hooks/useUserProfile'
import { EvidenceDocument } from '../documents.types'
import { downloadDocument } from '../service/documents.service'

type DocumentRowActionsProps = {
  document: EvidenceDocument
  // Preserved on the link so opening a document keeps the list's filters/sort/page intact.
  search: string
  onEdit: (document: EvidenceDocument) => void
  onDelete: (document: EvidenceDocument) => void
}

export const DocumentRowActions = ({
  document,
  search,
  onEdit,
  onDelete,
}: DocumentRowActionsProps) => {
  const { data: user } = useAtomValue(userAtom)
  const isAdmin = useAtomValue(isAdminAtom)
  // Owner always deletes their own; admin deletes any document. Client-side only — the
  // backend remains the actual enforcement boundary.
  const canDelete = isAdmin || document.ownerId === user?.user_uuid

  const handleShareLink = async () => {
    const url = `${window.location.origin}/app/documents/${document.id}`
    try {
      await navigator.clipboard.writeText(url)
      toast({ description: 'Link copied to clipboard.', position: 'top-right', type: 'success' })
    } catch {
      toast({ description: url, position: 'top-right', type: 'info' })
    }
  }

  const handleDownload = () => {
    downloadDocument(document.id, document.file.originalFilename).catch(() =>
      toast({ description: 'Failed to download document.', position: 'top-right', type: 'error' }),
    )
  }

  return (
    <div className="flex items-center justify-end gap-1">
      <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
        <Link
          to={{ pathname: `${appLink.documents.path}/${document.id}`, search }}
          title="View document"
          aria-label="View document"
        >
          <Eye className="h-4 w-4" />
        </Link>
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="h-7 w-7"
        onClick={handleDownload}
        title="Download"
        aria-label="Download document"
      >
        <Download className="h-4 w-4" />
      </Button>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="ghost" size="icon" className="h-7 w-7">
            <MoreVertical className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={() => onEdit(document)}>
            <Pencil className="h-3.5 w-3.5" />
            Edit document details
          </DropdownMenuItem>
          <DropdownMenuItem onClick={handleShareLink}>
            <Link2 className="h-3.5 w-3.5" />
            Share as a link
          </DropdownMenuItem>
          {canDelete && (
            <DropdownMenuItem onClick={() => onDelete(document)} className="text-destructive">
              <Trash2 className="h-3.5 w-3.5" />
              Delete document
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  )
}
